﻿import { useEffect, useState } from 'react';
import {
  Page, Card, Button, Banner, Text, TextField, Checkbox,
  Layout, BlockStack, InlineStack, Box, Spinner, DataTable, Pagination, InlineGrid,
} from '@shopify/polaris';
import { EmailIcon } from '@shopify/polaris-icons';
import { useExitIntentPopup } from '../../hooks/useExitIntentPopup';
import { EXCLUDABLE_PAGE_TYPES, type ExitIntentPopupSettings } from '../../api/exitIntentPopup';
import { StatCard } from '../shared/StatCard';

function LivePreview({ settings }: { settings: ExitIntentPopupSettings }) {
  return (
    <div style={{ background: 'rgba(0,0,0,0.5)', padding: 24, borderRadius: 8, display: 'flex', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 10, padding: '28px 24px', maxWidth: 320, width: '100%', textAlign: 'center', boxShadow: '0 12px 40px rgba(0,0,0,0.25)' }}>
        <Text as="h3" variant="headingMd">{settings.headline}</Text>
        <Box paddingBlockStart="200" paddingBlockEnd="300">
          <Text as="p" tone="subdued">{settings.body}</Text>
        </Box>
        <BlockStack gap="200">
          <div style={{ padding: '10px 12px', border: '1px solid #ccc', borderRadius: 6, textAlign: 'left', color: '#999' }}>Your email address</div>
          <div style={{ padding: '11px 18px', borderRadius: 6, background: '#008060', color: '#fff', fontWeight: 600 }}>{settings.button_text}</div>
        </BlockStack>
      </div>
    </div>
  );
}

export function ExitIntentPopupPanel() {
  const {
    settings, leadsCount, leads, leadsPage, leadsLastPage,
    loading, saving, error, fetchSettings, save, fetchLeads, setError,
  } = useExitIntentPopup();

  const [form, setForm] = useState<ExitIntentPopupSettings | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => { fetchSettings(); fetchLeads(1); }, [fetchSettings, fetchLeads]);
  useEffect(() => { if (settings) setForm(settings); }, [settings]);

  if (loading) {
    return (
      <Page fullWidth title="Exit-Intent Popup & Email Capture">
        <Layout>
          <Layout.Section>
            <InlineStack align="center"><Spinner /></InlineStack>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (!form) {
    return (
      <Page fullWidth title="Exit-Intent Popup & Email Capture">
        <Layout>
          <Layout.Section>
            <Banner tone="critical" action={{ content: 'Retry', onAction: fetchSettings }}>
              <p>{error ?? 'Failed to load exit-intent popup settings.'}</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const handleSave = async () => {
    setSaved(false);
    const result = await save(form);
    if (result) setSaved(true);
  };

  return (
    <Page fullWidth
      title="Exit-Intent Popup & Email Capture"
      subtitle="Show a discount offer when a visitor is about to leave, and capture their email for remarketing"
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          )}
          {saved && (
            <Banner tone="success" onDismiss={() => setSaved(false)}>
              <p>Settings saved. Changes appear on your storefront within about a minute.</p>
            </Banner>
          )}

          <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
            <StatCard label="Emails Captured" value={String(leadsCount)} icon={EmailIcon} iconColor="pink" />
            <StatCard
              label="Widget Status"
              value={form.enabled ? 'Enabled' : 'Disabled'}
              icon={EmailIcon}
              iconColor={form.enabled ? 'green' : 'indigo'}
            />
          </InlineGrid>

          <Card>
            <BlockStack gap="400">
              <Checkbox
                label="Show the exit-intent popup on my storefront"
                checked={form.enabled}
                onChange={(v) => setForm({ ...form, enabled: v })}
              />

              <TextField label="Headline" autoComplete="off" value={form.headline} onChange={(v) => setForm({ ...form, headline: v })} />
              <TextField label="Body text" autoComplete="off" multiline={2} value={form.body} onChange={(v) => setForm({ ...form, body: v })} />
              <TextField
                label="Discount code (optional)"
                helpText="Shown to the shopper after they submit their email. Leave blank to just say thanks."
                autoComplete="off"
                value={form.discount_code ?? ''}
                onChange={(v) => setForm({ ...form, discount_code: v || null })}
              />
              <TextField label="Button text" autoComplete="off" value={form.button_text} onChange={(v) => setForm({ ...form, button_text: v })} />
              <TextField
                label="Fallback trigger delay (seconds)"
                helpText="If exit-intent and the back-button trigger don't fire, the popup shows after this many seconds instead"
                type="number"
                autoComplete="off"
                value={String(form.trigger_delay_seconds)}
                onChange={(v) => setForm({ ...form, trigger_delay_seconds: Number(v) || 30 })}
              />

              <Box>
                <Button variant="primary" loading={saving} onClick={handleSave}>Save Settings</Button>
              </Box>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Frequency &amp; Targeting</Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Controls when a shopper who's already seen the popup gets left alone, and which pages it never shows on.
              </Text>

              <InlineStack gap="300">
                <Box minWidth="220px">
                  <TextField
                    label="Don't show again after subscribing (days)"
                    type="number"
                    autoComplete="off"
                    value={String(form.suppress_after_conversion_days)}
                    onChange={(v) => setForm({ ...form, suppress_after_conversion_days: Number(v) || 90 })}
                  />
                </Box>
                <Box minWidth="220px">
                  <TextField
                    label="Don't show again after closing without subscribing (days)"
                    type="number"
                    autoComplete="off"
                    value={String(form.reshow_after_dismiss_days)}
                    onChange={(v) => setForm({ ...form, reshow_after_dismiss_days: Number(v) || 7 })}
                  />
                </Box>
              </InlineStack>

              <BlockStack gap="150">
                <Text as="p" fontWeight="semibold">Never show on these pages</Text>
                {EXCLUDABLE_PAGE_TYPES.map((pt) => (
                  <Checkbox
                    key={pt.value}
                    label={pt.label}
                    checked={form.excluded_page_types.includes(pt.value)}
                    onChange={(checked) => setForm({
                      ...form,
                      excluded_page_types: checked
                        ? [...form.excluded_page_types, pt.value]
                        : form.excluded_page_types.filter((v) => v !== pt.value),
                    })}
                  />
                ))}
              </BlockStack>

              <Box>
                <Button variant="primary" loading={saving} onClick={handleSave}>Save Settings</Button>
              </Box>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">Live Preview</Text>
              <LivePreview settings={form} />
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Captured Emails ({leadsCount})</Text>
              {leads.length === 0 ? (
                <Text as="p" tone="subdued">No emails captured yet.</Text>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text']}
                  headings={['Email', 'Captured']}
                  rows={leads.map((l) => [l.email, l.created_at ? new Date(l.created_at).toLocaleDateString() : '—'])}
                />
              )}
              {leadsLastPage > 1 && (
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={leadsPage > 1}
                    onPrevious={() => fetchLeads(leadsPage - 1)}
                    hasNext={leadsPage < leadsLastPage}
                    onNext={() => fetchLeads(leadsPage + 1)}
                  />
                </InlineStack>
              )}
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd">One more step: enable it in your theme</Text>
              <Text as="p" tone="subdued">
                Go to <strong>Online Store &rarr; Themes &rarr; Customize &rarr; App embeds</strong>, then enable{' '}
                <strong>Exit-Intent Popup</strong>. This is a one-time toggle per theme.
              </Text>
            </BlockStack>
          </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
